import { ApprovalMode } from 'openapi';

import { actionButtonBaseStyle, chipBaseStyle } from 'styles/components/Common';
import {
  APPROVAL_BORDER_RADIUS_SM,
  APPROVAL_BORDER_RADIUS_MD,
  APPROVAL_BG_LIGHT,
  APPROVAL_BG_MEDIUM,
  APPROVAL_BG_MUTED,
  APPROVAL_BG_WHITE,
  APPROVAL_BORDER_LIGHT,
  APPROVAL_BORDER_SLIGHT,
  APPROVAL_BORDER_DASHED
} from 'styles/shared/ApprovalStyleUtils';

export const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
};

export const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: APPROVAL_BG_LIGHT,
  border: APPROVAL_BORDER_LIGHT,
  borderTopLeftRadius: APPROVAL_BORDER_RADIUS_MD,
  borderTopRightRadius: APPROVAL_BORDER_RADIUS_MD,
  padding: '0.375rem 1.25rem'
} as const;

export const scrollableContainerStyle = {
  flex: 1,
  overflow: 'auto',
  padding: '1rem',
  backgroundColor: APPROVAL_BG_MEDIUM,
  border: APPROVAL_BORDER_LIGHT,
  borderTop: 'none',
  borderBottom: 'none'
} as const;

export const helperBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  padding: '0.375rem 0.625rem',
  marginBottom: '1rem',
  borderRadius: APPROVAL_BORDER_RADIUS_MD,
  backgroundColor: APPROVAL_BG_WHITE,
  border: APPROVAL_BORDER_LIGHT
} as const;

export const helperInfoIconStyle = {
  fontSize: '1rem',
  color: '#9ca3af'
} as const;

export const helperTextStyle = {
  fontSize: '0.75rem',
  color: '#6b7280'
} as const;

export const rowBaseStyle = {
  display: 'flex',
  alignItems: 'center',
  height: 'auto',
  minHeight: '3.125rem',
  padding: '0.625rem 0.75rem 0.625rem 0.375rem',
  border: 'none',
  cursor: 'grab',
  backgroundColor: APPROVAL_BG_WHITE,
  '&:hover': {
    backgroundColor: APPROVAL_BG_MEDIUM
  },
  '&:active': { cursor: 'grabbing' }
} as const;

export const dragHandleStyle = {
  mr: 2,
  color: '#d1d5db',
  cursor: 'grab',
  alignSelf: 'center',
  fontSize: '1rem'
};

export const deleteButtonStyle = {
  ...actionButtonBaseStyle,
  backgroundColor: '#d32f2f',
  color: 'white',
  '&:hover': {
    backgroundColor: '#b71c1c'
  }
} as const;

export const cancelButtonStyle = {
  ...actionButtonBaseStyle,
  borderColor: '#e5e7eb',
  color: '#6b7280',
  '&:hover': {
    borderColor: '#d1d5db',
    backgroundColor: '#f3f4f6'
  }
} as const;

// Chip styles
export const chipGrayStyle = {
  ...chipBaseStyle,
  backgroundColor: '#f3f4f6',
  color: '#6b7280'
} as const;

export const chipAutoApproveStyle = {
  ...chipBaseStyle,
  backgroundColor: '#f0fdf4',
  color: '#15803d',
  border: '1px solid #15803d26'
} as const;

export const chipSequentialStyle = {
  ...chipBaseStyle,
  backgroundColor: '#eff6ff',
  color: '#1e40af',
  border: '1px solid #1e40af26'
} as const;

export const chipParallelStyle = {
  ...chipBaseStyle,
  backgroundColor: '#fef9e7',
  color: '#b45309',
  border: '1px solid #b4530926'
} as const;

export const chipExclusiveStyle = {
  ...chipBaseStyle,
  backgroundColor: '#fff7ed',
  color: '#dc2626'
} as const;

export const getChipStyle = (mode?: ApprovalMode): React.CSSProperties => {
  switch (mode) {
    case ApprovalMode.AUTO_APPROVE:
      return chipAutoApproveStyle;
    case ApprovalMode.SEQUENTIAL:
      return chipSequentialStyle;
    case ApprovalMode.PARALLEL:
      return chipParallelStyle;
    default:
      return {};
  }
};

// Container and loading styles
export const rulesListContainerStyle = {
  width: '100%',
  boxShadow: 'none !important',
  border: APPROVAL_BORDER_SLIGHT,
  borderRadius: APPROVAL_BORDER_RADIUS_MD
} as const;

export const defaultRuleContainerStyle = {
  width: '100%',
  boxShadow: 'none !important',
  border: APPROVAL_BORDER_DASHED,
  borderRadius: APPROVAL_BORDER_RADIUS_MD,
  mt: '1rem',
  '&:hover': {
    borderColor: '#078DEE'
  }
} as const;

// Rule-specific styles
export const rulePriorityStyle = {
  fontSize: '0.625rem',
  fontWeight: 700,
  color: '#c4c9d4',
  minWidth: '1rem',
  minHeight: '1.25rem',
  mr: '0.5rem',
  alignSelf: 'center',
  alignContent: 'center'
} as const;

export const ruleNameStyle = {
  fontSize: '0.75rem',
  fontWeight: 600
};

export const ruleContentBoxStyle = {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: '0.1875rem'
} as const;

export const ruleHeaderBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem'
};

export const conditionWrapperStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  mt: '0.1875rem',
  flexWrap: 'wrap'
} as const;

export const conditionLabelStyle = {
  color: '#c4c9d4',
  fontSize: '0.5625rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  mr: '0.125rem'
};

export const conditionBoxStyle = {
  backgroundColor: '#f3f4f6',
  border: '1px solid #e9eaec',
  padding: '0.125rem 0.375rem',
  borderRadius: APPROVAL_BORDER_RADIUS_SM
};

export const deleteConfirmationBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem'
};

export const defaultRuleContentStyle = {
  ...rowBaseStyle,
  display: 'flex',
  alignItems: 'center',
  padding: '0.625rem 1rem',
  gap: '0.75rem',
  cursor: 'default',
  backgroundColor: APPROVAL_BG_MUTED,
  borderRadius: APPROVAL_BORDER_RADIUS_MD,
  '&:active': { cursor: 'pointer' },
  '&:hover': {
    backgroundColor: '#f5f9ff',
    cursor: 'pointer'
  }
};
